<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Analysis Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <basic-heading :title="header" class="mb-3">
      <template #right-content>
        <header-button-group :button_config="button_config">
          <template #post_buttons>
            <button
              v-show="checkPermission('delete analysis')"
              title="Delete analysis"
              class="btn btn-danger ml-1"
              @click="showDeleteModal"
            >
              <i class="fal fa-trash" />
            </button>
          </template>
        </header-button-group>
      </template>
    </basic-heading>
    <permission-alert-gate :permissions="'read analysis'">
      <template #gated-content>
        <analysis-card v-if="analysis_id" :analysis="analysis" />
        <analysis-detail-cardstack />
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import AnalysisCard from "@/components/lab/analysis/AnalysisCard";
import AnalysisDetailCardstack from "@/components/lab/analysis/AnalysisDetailCardstack";
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { analysisNotifications } from "@/shared/notifications";
import { getModel } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { notificationOptions } from "@/shared/toast-notification-options";

Vue.use(VueNotifications, notificationOptions);

const AnalysisRepository = LabRepositoryFactory.get("analysis");

export default {
  name: "AnalysisDetail",
  components: {
    AnalysisCard,
    AnalysisDetailCardstack,
    BasicHeading,
    HeaderButtonGroup,
    PermissionAlertGate,
  },
  data() {
    return {
      loading: true,
      analysis_id: this.$route.params.analysis_id || null,
      analysis: null,
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-search",
            linkto: `/analysis/${this.analysis_id}/audit-trail`,
            title: "Audit Trail",
            variant: "dark",
          },
          {
            icon: "fa-list",
            linkto: "/analysis",
            title: "Analysis list",
          },
          {
            icon: "fa-pencil",
            linkto: `/analysis/${this.analysis_id}/edit`,
            title: "Edit analysis",
            variant: "warning",
            permission: this.checkPermission("edit analysis"),
          },
        ],
      };
    },
    header() {
      const { analyte } = this.analysis;
      const analyte_name =
        analyte && analyte.sample_type ? analyte.sample_type.name : "";

      return `${analyte_name} ${
        this.analysis &&
        (this.analysis.measurement_method || this.analysis.name)
      }`;
    },
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this analysis. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(async (confirm) => {
          if (confirm) {
            try {
              await AnalysisRepository.suspend(this.analysis_id);
  
              this.deleteSuccess();
              this.$router.push("/analysis");
            } catch ({ response }) {
              const { status, data } = response.response;
  
              this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
                title: "Server Error.",
                variant: "danger",
              });
            }
          }
        });
    },
  },
  async created() {
    this.loading = true;

    this.analysis = await getModel({
      model_type: "analysis",
      id: this.analysis_id,
      message: this.$bvToast,
    });

    this.analysis ? (this.loading = false) : this.$router.back();
  },
  notifications: analysisNotifications,
};
</script>
